import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Edit,
  Trash2,
  Eye,
  Search,
  X,
  BookOpen,
  Tag,
  Calendar,
  Filter,
  RefreshCw,
  Plus,
  Upload,
  Copy,
  CheckCircle,
} from "lucide-react";
import { Pagination } from "@mui/material";
import { server } from "../server";
import { toast } from "react-toastify";

export default function QuizQuestions() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [filterTopics, setFilterTopics] = useState([]); // Topics for the main filter
  const [formTopics, setFormTopics] = useState([]); // Topics for the add/edit form
  const [filterSubject, setFilterSubject] = useState("");
  const [filterTopic, setFilterTopic] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [modalType, setModalType] = useState(""); // "view" or "edit" or "add" or "bulk"
  const [totalCount, setTotalCount] = useState(0);

  // Bulk Upload State
  const [bulkJson, setBulkJson] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const itemsPerPage = 12;

  // New Question State
  const [formData, setFormData] = useState({
    question: "",
    correctAnswer: "",
    options: ["", "", "", ""],
    subjectId: "",
    topicId: "",
    difficulty: "medium",
  });

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Fetch dropdown data (Subjects only)
  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/quizSubject/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
          deleted: false,
        },
      });
      setSubjects(data?.data || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  // Helper to fetch topics by subject
  const fetchTopicsBySubject = async (subjectId, setState) => {
    try {
      const params = { pageNo: 1, pageSize: 100, deleted: false }; // Ensure we get enough topics
      if (subjectId) params.subjectId = subjectId;

      const { data } = await axios.get(`${server}/quizTopic/all`, { params });
      setState(data.data || []);
    } catch (error) {
      console.error("Error fetching topics:", error);
      setState([]);
    }
  };

  const fetchQuestions = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        pageNo: page,
        pageSize: itemsPerPage,
      };

      if (searchTerm) params.searchParams = searchTerm;
      if (filterSubject) params.subjectId = filterSubject;
      if (filterTopic) params.topicId = filterTopic;

      const { data } = await axios.get(`${server}/quizQuestion/all`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setQuestions(data?.data || []);
      setTotalCount(data?.count || 0);
      setCurrentPage(page);
    } catch (err) {
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
    fetchTopicsBySubject(null, setFilterTopics); // Initial topics for filter
    fetchQuestions(1);
  }, []);

  // Update filter topics when filter subject changes
  useEffect(() => {
    fetchTopicsBySubject(filterSubject, setFilterTopics);
    setFilterTopic(""); // Reset topic filter when subject changes
  }, [filterSubject]);

  // Update form topics when form subject changes
  useEffect(() => {
    if (formData.subjectId) {
      fetchTopicsBySubject(formData.subjectId, setFormTopics);
    } else {
      setFormTopics([]);
    }
  }, [formData.subjectId]);

  useEffect(() => {
    fetchQuestions(1);
  }, [searchTerm, filterSubject, filterTopic]);

  const handlePageChange = (event, value) => {
    fetchQuestions(value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterSubject("");
    setFilterTopic("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${server}/quizQuestion/deleteById/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchQuestions(currentPage);
      toast.success("Question deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleOpenModal = (type, question = null) => {
    setModalType(type);
    if (type === "edit" && question) {
      setSelectedQuestion(question);
      setFormData({
        question: question.question,
        correctAnswer: question.correctAnswer || question.answer,
        options: question.options || ["", "", "", ""],
        subjectId: question.subjectId,
        topicId: question.topicId,
        difficulty: question.difficulty || "medium",
      });
    } else if (type === "add") {
      setFormData({
        question: "",
        correctAnswer: "",
        options: ["", "", "", ""],
        subjectId: "",
        topicId: "",
        difficulty: "medium",
      });
      setFormTopics([]);
    } else if (type === "bulk") {
      setFormData({
        question: "",
        correctAnswer: "",
        options: [],
        subjectId: "",
        topicId: "",
        difficulty: "medium",
      });
      setBulkJson("");
      setFormTopics([]);
    } else if (type === "view") {
      setSelectedQuestion(question);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        question: formData.question,
        options: formData.options,
        correctAnswer: formData.correctAnswer,
        subjectId: parseInt(formData.subjectId),
        topicId: formData.topicId ? parseInt(formData.topicId) : null,
        difficulty: formData.difficulty,
      };

      if (modalType === "add") {
        await axios.post(`${server}/quizQuestion/new`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        toast.success("Question added successfully");
      } else if (modalType === "edit") {
        await axios.put(
          `${server}/quizQuestion/updateById`,
          {
            id: selectedQuestion.id,
            ...payload,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        toast.success("Question updated successfully");
      }
      setModalType("");
      fetchQuestions(currentPage);
    } catch (err) {
      toast.error("Operation failed");
      console.error(err);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subjectId) {
      toast.error("Please select a subject");
      return;
    }

    try {
      const parsedData = JSON.parse(bulkJson);
      if (!Array.isArray(parsedData)) {
        toast.error("JSON data must be an array of questions");
        return;
      }

      setBulkSubmitting(true);
      let successCount = 0;
      let errorCount = 0;

      for (const item of parsedData) {
        try {
          const payload = {
            question: item.question,
            options: item.options,
            correctAnswer: item.correctAnswer,
            subjectId: parseInt(formData.subjectId),
            topicId: formData.topicId ? parseInt(formData.topicId) : null, // Optional topic for bulk
            difficulty: formData.difficulty || "medium",
          };

          await axios.post(`${server}/quizQuestion/new`, payload, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          successCount++;
        } catch (err) {
          console.error("Bulk upload error for item:", item, err);
          errorCount++;
        }
      }

      toast.success(
        `Bulk upload complete: ${successCount} added, ${errorCount} failed`
      );
      setModalType("");
      fetchQuestions(1);
    } catch (err) {
      toast.error("Invalid JSON format");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const copyTemplate = () => {
    const template = [
      {
        question: "Example Question?",
        options: ["Option A", "Option B", "Option C", "Option D"],
        correctAnswer: "Option A",
      },
      {
        question: "Another Question?",
        options: ["Yes", "No", "Maybe", "Can't say"],
        correctAnswer: "Yes",
      },
    ];
    navigator.clipboard.writeText(JSON.stringify(template, null, 2));
    toast.success("Template copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6 relative">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Quiz Questions</h1>
          <p className="text-gray-400 text-sm mt-1">
            Total: {totalCount} questions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleOpenModal("bulk")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all duration-200 border border-gray-600"
          >
            <Upload size={16} />
            Bulk Upload
          </button>
          <button
            onClick={() => handleOpenModal("add")}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all duration-200"
          >
            <Plus size={16} />
            Add Question
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                className="pl-9 pr-3 py-2 border border-gray-600 rounded-lg w-full text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subject
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
            >
              <option value="">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.title || subject.subject}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Topic
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
            >
              <option value="">All Topics</option>
              {filterTopics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.topic || topic.title || topic.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw size={16} />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          No questions found
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <AnimatePresence>
              {questions.map((q) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-gray-600 transition-all duration-200"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 pr-4">
                      <h2 className="font-semibold text-white text-lg leading-tight line-clamp-2 mb-2">
                        {q.question}
                      </h2>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleOpenModal("view", q)}
                        className="text-gray-400 hover:text-blue-400 p-1"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleOpenModal("edit", q)}
                        className="text-gray-400 hover:text-green-400 p-1"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="text-gray-400 hover:text-red-400 p-1"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    Answer: {q.answer}
                  </p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#E5E7EB",
                    "&.Mui-selected": {
                      background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                      color: "white",
                    },
                  },
                }}
              />
            </div>
          )}
        </>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modalType && (
          <div
            className={`fixed inset-0 bg-black bg-opacity-60 flex justify-center z-50 p-4 ${
              ["bulk", "add", "edit"].includes(modalType)
                ? "items-start pt-10"
                : "items-center"
            }`}
            onClick={() => setModalType("")}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
                <h3 className="text-xl font-semibold text-white">
                  {modalType === "view"
                    ? "Question Details"
                    : modalType === "edit"
                    ? "Edit Question"
                    : modalType === "bulk"
                    ? "Bulk Upload Questions"
                    : "Add Question"}
                </h3>
                <button onClick={() => setModalType("")}>
                  <X size={24} className="text-gray-400" />
                </button>
              </div>

              <div className="p-6">
                {modalType === "view" && selectedQuestion ? (
                  <div className="space-y-4 text-gray-300">
                    <p>
                      <strong>Question:</strong> {selectedQuestion.question}
                    </p>
                    <p>
                      <strong>Answer:</strong> {selectedQuestion.answer}
                    </p>
                    <p>
                      <strong>Options:</strong>{" "}
                      {selectedQuestion.options.join(", ")}
                    </p>
                    <p>
                      <strong>Subject:</strong>{" "}
                      {subjects.find((s) => s.id === selectedQuestion.subjectId)
                        ?.title || selectedQuestion.subjectId}
                    </p>
                  </div>
                ) : modalType === "bulk" ? (
                  <form onSubmit={handleBulkSubmit} className="space-y-4">
                    <p className="text-gray-400 text-sm">
                      Paste a JSON array of questions. Select Subject and Topic
                      to apply to all questions.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Subject <span className="text-red-500">*</span>
                        </label>
                        <select
                          className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                          value={formData.subjectId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              subjectId: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.title || s.subject}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Topic (Optional)
                        </label>
                        <select
                          className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                          value={formData.topicId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              topicId: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Topic</option>
                          {formTopics.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.topic || t.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="block text-sm text-gray-300">
                          JSON Payload
                        </label>
                        <button
                          type="button"
                          onClick={copyTemplate}
                          className="text-xs flex items-center gap-1 text-blue-400 hover:text-blue-300"
                        >
                          <Copy size={12} /> Copy Template
                        </button>
                      </div>
                      <textarea
                        rows={10}
                        className="w-full p-3 bg-gray-700 rounded-lg text-white border border-gray-600 font-mono text-sm"
                        value={bulkJson}
                        onChange={(e) => setBulkJson(e.target.value)}
                        placeholder='[
  {
    "question": "Sample Question?",
    "options": ["A", "B", "C", "D"],
    "correctAnswer": "A"
  }
]'
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={bulkSubmitting}
                      className="w-full py-2 bg-green-600 rounded text-white font-semibold flex justify-center items-center gap-2 hover:bg-green-700 disabled:opacity-50"
                    >
                      {bulkSubmitting ? (
                        <>
                          <RefreshCw className="animate-spin w-4 h-4" />{" "}
                          Processing...
                        </>
                      ) : (
                        <>
                          <Upload size={18} /> Upload Questions
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  // Add / Edit Form
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">
                        Question
                      </label>
                      <input
                        type="text"
                        className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                        value={formData.question}
                        onChange={(e) =>
                          setFormData({ ...formData, question: e.target.value })
                        }
                        required
                      />
                    </div>

                    {/* Options */}
                    <div>
                      <label className="block text-sm text-gray-300 mb-1">
                        Options
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {formData.options.map((option, index) => (
                          <div key={index} className="flex gap-2">
                            <span className="text-gray-400 py-2 w-6">
                              {index + 1}.
                            </span>
                            <input
                              type="text"
                              className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                              placeholder={`Option ${index + 1}`}
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...formData.options];
                                newOptions[index] = e.target.value;
                                setFormData({
                                  ...formData,
                                  options: newOptions,
                                });
                              }}
                              required
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-300 mb-1">
                        Correct Answer
                      </label>
                      <select
                        className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                        value={formData.correctAnswer}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            correctAnswer: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Correct Answer</option>
                        {formData.options.map(
                          (opt, i) =>
                            opt && (
                              <option key={i} value={opt}>
                                {opt}
                              </option>
                            )
                        )}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Subject
                        </label>
                        <select
                          className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                          value={formData.subjectId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              subjectId: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Select Subject</option>
                          {subjects.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.title || s.subject}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-1">
                          Topic
                        </label>
                        <select
                          className="w-full p-2 bg-gray-700 rounded text-white border border-gray-600"
                          value={formData.topicId}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              topicId: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Topic</option>
                          {formTopics.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.topic || t.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-2 bg-blue-600 rounded text-white font-semibold"
                    >
                      Save Question
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
